import { useState, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import type { OrderWithTimeline } from "../page";

const WHATSAPP_FN_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/whatsapp-notify`;

async function sendReviewNotification(params: {
  phone: string;
  name: string;
  orderRef: string;
  reviewCount: number;
  productNames: string[];
  averageRating: number;
}) {
  try {
    await fetch(WHATSAPP_FN_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "review_submitted",
        phone: params.phone,
        name: params.name,
        orderRef: params.orderRef,
        reviewCount: params.reviewCount,
        productNames: params.productNames,
        averageRating: params.averageRating,
      }),
    });
  } catch {
    // silent — notification is best-effort
  }
}

interface Props {
  order: OrderWithTimeline;
  onClose: () => void;
}

interface ProductReview {
  productId: number;
  productName: string;
  productImage: string;
  rating: number;
  reviewText: string;
  submitted: boolean;
  submitting: boolean;
  error: string;
}

const STAR_LABELS: Record<number, string> = {
  1: "سيء",
  2: "مقبول",
  3: "جيد",
  4: "جيد جداً",
  5: "ممتاز",
};

function StarRating({
  rating,
  onChange,
  disabled,
}: {
  rating: number;
  onChange: (r: number) => void;
  disabled: boolean;
}) {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          onClick={() => onChange(star)}
          onMouseEnter={() => !disabled && setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all cursor-pointer ${
            disabled ? "cursor-default opacity-60" : "hover:bg-amber-400/10"
          }`}
        >
          <i
            className={`ri-star-fill text-xl transition-colors ${
              (hovered || rating) >= star ? "text-amber-400" : "text-white/10"
            }`}
          ></i>
        </button>
      ))}
      {(hovered || rating) > 0 && (
        <span
          className="text-amber-400 text-xs font-bold mr-1"
          style={{ fontFamily: "'Cairo', sans-serif" }}
        >
          {STAR_LABELS[hovered || rating]}
        </span>
      )}
    </div>
  );
}

export default function ReviewOrderModal({ order, onClose }: Props) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [reviews, setReviews] = useState<ProductReview[]>(
    order.items.map((item) => ({
      productId: item.id,
      productName: item.nameAr,
      productImage: item.image ?? "",
      rating: 0,
      reviewText: "",
      submitted: false,
      submitting: false,
      error: "",
    }))
  );
  const [allDone, setAllDone] = useState(false);
  const notifSentRef = useRef(false);

  const current = reviews[currentIdx];

  const updateCurrent = useCallback(
    (patch: Partial<ProductReview>) => {
      setReviews((prev) =>
        prev.map((r, i) => (i === currentIdx ? { ...r, ...patch } : r))
      );
    },
    [currentIdx]
  );

  const handleSubmitCurrent = useCallback(async () => {
    if (current.rating === 0) {
      updateCurrent({ error: "يرجى اختيار تقييم بالنجوم أولاً" });
      return;
    }
    if (current.reviewText.trim().length < 5) {
      updateCurrent({ error: "يرجى كتابة تعليق لا يقل عن 5 أحرف" });
      return;
    }

    updateCurrent({ submitting: true, error: "" });

    try {
      // Check duplicate
      const { data: existing } = await supabase
        .from("reviews")
        .select("id")
        .eq("product_id", current.productId)
        .eq("reviewer_phone", order.phone)
        .maybeSingle();

      if (existing) {
        updateCurrent({ submitting: false, submitted: true, error: "" });
        goNext();
        return;
      }

      const { error: insertError } = await supabase.from("reviews").insert({
        product_id: current.productId,
        customer_id: order.customerId ?? null,
        reviewer_name: order.name,
        reviewer_phone: order.phone,
        rating: current.rating,
        review_text: current.reviewText.trim(),
        is_verified: true,
      });

      if (insertError) {
        updateCurrent({ submitting: false, error: "حدث خطأ أثناء الإرسال، حاول مرة أخرى" });
        return;
      }

      updateCurrent({ submitting: false, submitted: true, error: "" });
      goNext();
    } catch {
      updateCurrent({ submitting: false, error: "حدث خطأ غير متوقع" });
    }
  }, [current, order, updateCurrent]);

  const goNext = useCallback(() => {
    const nextIdx = currentIdx + 1;
    if (nextIdx >= reviews.length) {
      setAllDone(true);
      // Send WhatsApp thank-you notification once
      if (!notifSentRef.current) {
        notifSentRef.current = true;
        const submitted = reviews.filter((r) => r.submitted || (r === reviews[currentIdx]));
        const productNames = submitted.map((r) => r.productName);
        const ratings = submitted.map((r) => r.rating).filter((r) => r > 0);
        const avgRating = ratings.length > 0
          ? Math.round((ratings.reduce((s, r) => s + r, 0) / ratings.length) * 10) / 10
          : 5;
        sendReviewNotification({
          phone: order.phone,
          name: order.name,
          orderRef: order.orderRef,
          reviewCount: submitted.length,
          productNames,
          averageRating: avgRating,
        });
      }
    } else {
      setCurrentIdx(nextIdx);
    }
  }, [currentIdx, reviews, order]);

  const handleSkip = useCallback(() => {
    goNext();
  }, [goNext]);

  const submittedCount = reviews.filter((r) => r.submitted).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.85)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="bg-[#141414] border border-white/8 rounded-2xl w-full max-w-lg overflow-hidden"
        style={{ maxHeight: "90vh", overflowY: "auto" }}
      >
        {/* Header */}
        <div className="relative p-6 border-b border-white/5">
          <div className="h-1 absolute top-0 left-0 right-0 bg-gradient-to-r from-amber-400/0 via-amber-400/80 to-amber-400/0 rounded-t-2xl"></div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-400/10 rounded-xl">
                <i className="ri-star-smile-line text-amber-400 text-lg"></i>
              </div>
              <div>
                <h2
                  className="text-white font-black text-base"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  قيّم طلبك
                </h2>
                <p
                  className="text-gray-500 text-xs mt-0.5"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  طلب #{order.orderRef} · {order.items.length}{" "}
                  {order.items.length === 1 ? "منتج" : "منتجات"}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-gray-500 hover:text-white transition-all cursor-pointer"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>

          {/* Progress dots */}
          {reviews.length > 1 && !allDone && (
            <div className="flex items-center gap-2 mt-4">
              {reviews.map((r, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    r.submitted
                      ? "bg-green-400 flex-1"
                      : i === currentIdx
                      ? "bg-amber-400 flex-1"
                      : "bg-white/10 flex-1"
                  }`}
                ></div>
              ))}
            </div>
          )}
        </div>

        {/* All Done State */}
        {allDone ? (
          <div className="p-8 flex flex-col items-center text-center gap-5">
            <div className="w-20 h-20 flex items-center justify-center bg-green-400/10 border border-green-400/20 rounded-full">
              <i className="ri-check-double-line text-green-400 text-3xl"></i>
            </div>
            <div>
              <h3
                className="text-white font-black text-xl mb-2"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                شكراً على تقييمك! 🌟
              </h3>
              <p
                className="text-gray-400 text-sm leading-relaxed"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                تقييماتك تساعد العملاء الآخرين على اتخاذ قرارات أفضل
                <br />
                وتساعدنا على تحسين خدمتنا باستمرار
              </p>
            </div>
            <div className="flex items-center gap-2 bg-amber-400/5 border border-amber-400/15 rounded-xl px-4 py-3 w-full">
              <i className="ri-star-fill text-amber-400"></i>
              <p
                className="text-amber-400/80 text-sm font-bold"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                تم إرسال {submittedCount} تقييم بنجاح
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-full py-3.5 bg-amber-400 hover:bg-amber-500 text-black font-black rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              إغلاق
            </button>
          </div>
        ) : (
          /* Review Form */
          <div className="p-6 space-y-5">
            {/* Product card */}
            <div className="flex items-center gap-4 bg-[#1a1a1a] rounded-xl p-4 border border-white/5">
              <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-[#222] border border-white/5">
                {current.productImage ? (
                  <img
                    src={current.productImage}
                    alt={current.productName}
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-image-line text-gray-700 text-xl"></i>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="text-white font-bold text-sm truncate"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  {current.productName}
                </p>
                <p
                  className="text-gray-500 text-xs mt-1"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  {reviews.length > 1
                    ? `المنتج ${currentIdx + 1} من ${reviews.length}`
                    : "قيّم تجربتك مع هذا المنتج"}
                </p>
              </div>
              {current.submitted && (
                <div className="w-7 h-7 flex items-center justify-center bg-green-400/10 rounded-full flex-shrink-0">
                  <i className="ri-check-line text-green-400 text-sm"></i>
                </div>
              )}
            </div>

            {/* Stars */}
            <div>
              <p
                className="text-gray-400 text-xs mb-3 font-bold"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                تقييمك للمنتج
              </p>
              <StarRating
                rating={current.rating}
                onChange={(r) => updateCurrent({ rating: r, error: "" })}
                disabled={current.submitting}
              />
            </div>

            {/* Review text */}
            <div>
              <p
                className="text-gray-400 text-xs mb-2 font-bold"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                تعليقك
              </p>
              <textarea
                value={current.reviewText}
                onChange={(e) =>
                  updateCurrent({ reviewText: e.target.value, error: "" })
                }
                disabled={current.submitting}
                placeholder="شاركنا تجربتك مع هذا المنتج... الجودة، التوصيل، التغليف..."
                rows={4}
                maxLength={500}
                className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 resize-none focus:outline-none focus:border-amber-400/40 transition-colors disabled:opacity-50"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              />
              <div className="flex justify-between items-center mt-1">
                {current.error ? (
                  <p
                    className="text-red-400 text-xs"
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                  >
                    <i className="ri-error-warning-line ml-1"></i>
                    {current.error}
                  </p>
                ) : (
                  <span />
                )}
                <span
                  className={`text-xs ${
                    current.reviewText.length > 450
                      ? "text-amber-400"
                      : "text-gray-600"
                  }`}
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  {current.reviewText.length}/500
                </span>
              </div>
            </div>

            {/* Verified badge */}
            <div className="flex items-center gap-2 bg-green-400/5 border border-green-400/15 rounded-xl px-3 py-2.5">
              <i className="ri-shield-check-line text-green-400 text-sm"></i>
              <p
                className="text-green-400 text-xs"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                تقييمك سيظهر كـ{" "}
                <strong>مشتري موثّق</strong> لأنك اشتريت هذا المنتج فعلاً
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              {reviews.length > 1 && (
                <button
                  onClick={handleSkip}
                  disabled={current.submitting}
                  className="flex-1 py-3.5 bg-white/5 hover:bg-white/8 border border-white/8 text-gray-400 font-bold rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  تخطي
                </button>
              )}
              <button
                onClick={handleSubmitCurrent}
                disabled={current.submitting}
                className="flex-[2] py-3.5 bg-amber-400 hover:bg-amber-500 text-black font-black rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                {current.submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                    جاري الإرسال...
                  </>
                ) : currentIdx < reviews.length - 1 ? (
                  <>
                    إرسال والتالي
                    <i className="ri-arrow-left-line text-sm"></i>
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line text-sm"></i>
                    إرسال التقييم
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
